import { Navigate, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { isLoggedIn, clearTokens } from '../lib/auth'
import { api } from '../lib/api'
import { useEnabledApps } from '../lib/useEnabledApps'

type Props = {
  children: React.ReactNode
  /** Require user.is_staff OR is_superuser. If false, any authenticated user works. */
  adminOnly?: boolean
  /** If true, only non-admin users may access (kicks admins to /admin). */
  userOnly?: boolean
  /** User must have at least one of these permission codes (admins automatically pass). */
  requiredPermissions?: string[]
  /** Module code (core.App.code) — if the App row has is_enabled=false the
   *  route is blocked for EVERYONE including admins. Admins re-enable via
   *  /admin/apps (the Modules page). */
  requiredApp?: string
  /** Where to redirect if not authenticated. */
  loginPath?: string
}

export function ProtectedRoute({
  children,
  adminOnly = false,
  userOnly = false,
  requiredPermissions,
  requiredApp,
  loginPath = '/admin/login',
}: Props) {
  const location = useLocation()

  // Use the shared ['me'] cache (also used by AdminLayout etc.) so navigation
  // between protected routes doesn't refetch and doesn't cause a flicker.
  const { data: me, isLoading, isError } = useQuery({
    queryKey: ['me'],
    queryFn: async () => (await api.get('/auth/me/')).data,
    enabled: isLoggedIn(),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  })

  const { isEnabled: isAppEnabled, isLoading: appsLoading } = useEnabledApps()

  if (!isLoggedIn()) {
    return <Navigate to={loginPath} state={{ from: location }} replace />
  }

  if (isLoading || appsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-muted-foreground text-sm">Loading…</p>
      </div>
    )
  }

  if (isError || !me) {
    clearTokens()
    return <Navigate to={loginPath} state={{ from: location }} replace />
  }

  const isAdmin: boolean = !!(me.is_staff || me.is_superuser)
  const permissions: string[] = me.permissions || []

  if (adminOnly && !isAdmin) {
    return <Navigate to="/portal" replace />
  }
  if (userOnly && isAdmin) {
    return <Navigate to="/admin" replace />
  }
  // Module on/off — applies to admins AND end users. The Modules page itself
  // is not behind requiredApp, so admins can always re-enable a module.
  if (requiredApp && !isAppEnabled(requiredApp)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="max-w-sm text-center">
          <h2 className="text-lg font-semibold text-foreground mb-2">Module disabled</h2>
          <p className="text-sm text-muted-foreground mb-4">
            This module is currently switched off. {isAdmin ? (
              <>You can re-enable it from the <a href="/admin/apps" className="text-primary hover:underline">Modules</a> page.</>
            ) : (
              <>Contact your administrator if you need it back.</>
            )}
          </p>
          <button
            onClick={() => window.history.back()}
            className="text-sm text-primary hover:underline"
          >
            ← Go back
          </button>
        </div>
      </div>
    )
  }
  if (requiredPermissions && requiredPermissions.length > 0 && !isAdmin) {
    const hasAny = requiredPermissions.some((p) => permissions.includes(p))
    if (!hasAny) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="max-w-sm text-center">
            <h2 className="text-lg font-semibold text-foreground mb-2">Permission required</h2>
            <p className="text-sm text-muted-foreground mb-4">
              You don't have the permission needed to view this page. Contact your administrator
              if you think this is a mistake.
            </p>
            <button
              onClick={() => window.history.back()}
              className="text-sm text-primary hover:underline"
            >
              ← Go back
            </button>
          </div>
        </div>
      )
    }
  }

  return <>{children}</>
}
